import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { Subscription } from 'rxjs/internal/Subscription';
import { PlatformHub } from '../platform.hub';
import { ActorSendGroupStatusInformationModel } from '../models/api/actor-status-information.model';
import { EventLogModel } from './api-clients/event-log.api-client';
import { ExistsIn, SingleOrDefault } from '../_helpers/ArrayExtensionMethodHelper.functions';
import { DashboardApiClient } from './api-clients/dashboard.api-client';
import { DashboardRequestModel } from '../models/dashboard/dashboard-request.model';
import { BuilderRequestModel } from '../models/builder/builder-request.model';
import { StatusRequestModel } from '../models/status/status-request.model';
import { NoteModel } from '../models/notes/note.model';
import { AuthenticationService } from './authentication.service';

/**
 * Mapper/Handler Service for the SihgnalR HUB
 */
@Injectable({
    providedIn: 'root'
})
export class PlatformHubService implements OnDestroy {

    /**
     * Am i currently Starting up/Loading?
     */
    public loading: boolean = true;
    /**
     * Listener for when Loading Changes.
     * Emits True on loading/DEfault
     * Emits FALSE when finished
     */
    public loadingobserver: Subject<boolean> = new Subject<boolean>();

    // LISTENABLE SOURCES
    public statusRequest$: BehaviorSubject<StatusRequestModel> = new BehaviorSubject({} as StatusRequestModel);
    public builderRequest$: BehaviorSubject<BuilderRequestModel> = new BehaviorSubject({} as BuilderRequestModel);

    /**
     * Listener for when Request are deleted and thus need to be removed from Dashboard Source
     */
    private removeRequestsHubSubscription: Subscription = new Subscription();
    /**
     * Listener for Singular Updates to Dashboard Request
     */
    private dashboardUpdateSingleRequestHubSubscription: Subscription = new Subscription();
    /**
        * Listener for Singular Updates to Builder Request
        */
    private builderUpdateSingleRequestHubSubscription: Subscription = new Subscription(); /**
     * Listener for Singular Updates to Status Request
     */
    private statusUpdateSingleRequestHubSubscription: Subscription = new Subscription();

    // DASHBOARD ARRAY stuff:
    public requestSource: DashboardRequestModel[] = [];
    public dashboardRequests$: BehaviorSubject<DashboardRequestModel[]> = new BehaviorSubject(this.requestSource);
    /**
     * Monotonically increasing token used to guard against overlapping loadRequests() calls:
     * if a newer call is started before an older one resolves, the older (now stale)
     * response must be discarded so it can never overwrite state set by the newer one
     * (e.g. toggling "show all organisation documents" racing with an app-load/init reload).
     */
    private loadRequestsSequence: number = 0;
    /**
     * Whether the Requestsource of Dashboard has any Workgroups assigned
     */
    public get RequestsInWorkgroup(): boolean { return this.requestSource.some(p => p.workgroupGuid); };

    public get newNote$(): Observable<NoteModel> {
        return this.platformHub.newNote$;
    }
    public get updateNotes$(): Observable<NoteModel> {
        return this.platformHub.updateNotes$;
    }
    public get deletedNote$(): Observable<string> {
        return this.platformHub.deletedNote$;
    }

    constructor(private dashboardApiClient: DashboardApiClient, public platformHub: PlatformHub, private authenticationService: AuthenticationService) {
        // Setup Request deleted:
        this.removeRequestsHubSubscription = this.platformHub.requestGuidToRemoveSubject$.subscribe(requestGuid => {
            this.HandleRequestDeletedEvent(requestGuid);
        });
        // Setup Singular Dashboard Updates:
        this.dashboardUpdateSingleRequestHubSubscription = this.platformHub.singularRequestUpdatedSubject$.subscribe(requestDashboardModel => {
            this.HandleRequestUpdatedDashboardModel(requestDashboardModel);
        });
        // Setup Listener Status Updates:
        this.statusUpdateSingleRequestHubSubscription = this.platformHub.statusRequestUpdatedSubject$.subscribe(statusModel => {
            this.PushUpdatedStatusModel(statusModel);
        });
        // Setup Listener Builder Updates:
        this.builderUpdateSingleRequestHubSubscription = this.platformHub.builderRequestUpdatedSubject$.subscribe(buildermodel => {
            this.PushUpdatedBuilderModel(buildermodel);
        });
    }

    /**
     * Push model as Status model
     */
    private PushUpdatedStatusModel(model: StatusRequestModel) {
        this.statusRequest$.next(model);
    }
    /**
     * Push model as Status model
     */
    private PushUpdatedBuilderModel(model: BuilderRequestModel) {
        this.builderRequest$.next(model);
    }

    /**
     * Load requests for dashboard, and if true (re)starts the signalR link
     * @param openSignalrLink Open or Reopen link, usefull for when tokens change
     */
    public async start(openSignalrLink: boolean) {
        await this.loadRequests();
        if (openSignalrLink) {
            this.platformHub.start();
        }
        this.loading = false;
        this.loadingobserver.next(false);
        this.sortRequests();
    }

    /**
     * NEVER call this without also calling the STOPListening version
     * @param requestGuid
     * @returns
     */
    public ListenToActorList(requestGuid: string): Observable<ActorSendGroupStatusInformationModel[]> {
        this.platformHub.subscribeToRequestStatusGroup(requestGuid);
        return this.platformHub.actors$;
    }
    public StopListeningToActorList(requestGuid: string) {
        this.platformHub.unsubscribeToRequestStatusGroup(requestGuid);
    }

    /**
     * NEVER call this without also calling the STOPListening version
     * @param requestGuid
     * @returns
     */
    public ListenToEventLog(requestGuid: string): Observable<EventLogModel> {
        this.platformHub.subscribeToRequestEventLog(requestGuid);
        return this.platformHub.eventLog$;
    }
    public StopListeningToEventLog(requestGuid: string) {
        this.platformHub.unsubscribeToRequestEventLog(requestGuid);
    }


    /**
     * NEVER call this without also calling the STOPListening version
     * @param requestGuid
     * @returns
     */
    public ListenToNotes(requestGuid: string) {
        this.platformHub.subscribeToRequestNotes(requestGuid);
    }
    public StopListeningToNotes(requestGuid: string) {
        this.platformHub.unsubscribeToRequestNotes(requestGuid);
    }

    /**
       * LOAD request for the Dashboard Source
       *
       * Guarded against overlapping calls: if another loadRequests() call is started before
       * this one resolves, this (now stale) response is discarded instead of overwriting the
       * more recent state.
       */
    public async loadRequests() {
        const requestToken = ++this.loadRequestsSequence;
        let requests: DashboardRequestModel[] = [];
        requests = await this.dashboardApiClient.loadRequests();
        requests = requests.filter(x => x != null);

        if (requestToken !== this.loadRequestsSequence) {
            // A newer loadRequests() call has been started in the meantime; do not let this
            // stale response overwrite it.
            return;
        }

        this.requestSource = requests;
        this.assignWorkgroupNames();
        this.dashboardRequests$.next(this.requestSource);
    }

    ngOnDestroy() {
        if (this.removeRequestsHubSubscription) {
            this.removeRequestsHubSubscription.unsubscribe();
        }
        if (this.dashboardUpdateSingleRequestHubSubscription) {
            this.dashboardUpdateSingleRequestHubSubscription.unsubscribe();
        }
        if (this.statusUpdateSingleRequestHubSubscription) {
            this.statusUpdateSingleRequestHubSubscription.unsubscribe();
        }
        if (this.builderUpdateSingleRequestHubSubscription) {
            this.builderUpdateSingleRequestHubSubscription.unsubscribe();
        }


    }

    /**
     * Since this holds the Dashboard request, it can slo initial SORT them
     */
    private sortRequests() {
        this.requestSource.sort(
            function (a, b) { return new Date(b.created).getTime() - new Date(a.created).getTime(); }
        );
        this.dashboardRequests$.next(this.requestSource);
    }

    /**
     * Handles that a Request has been deleted and thus removing it from the Dashboard source (If matched)
     * @param requestGuid
     */
    private HandleRequestDeletedEvent(requestGuid: string) {
        let hasRequest = SingleOrDefault(this.requestSource, "requestId", requestGuid);
        if (hasRequest) {
            const index = this.requestSource.indexOf(hasRequest, 0);
            if (index > -1) {
                this.requestSource.splice(index, 1);
                this.dashboardRequests$.next(this.requestSource);
            }
        }

    }

    /**
     * Verifies whether model is a new Entry for dashboard, or an Existing one that must be updated.
     * and than updates the Dashboard request source with the new information
     * @param model
     */
    private HandleRequestUpdatedDashboardModel(model: DashboardRequestModel) {
        let hasRequest = ExistsIn(this.requestSource, "requestId", model.requestId);
        if (hasRequest) {
            let indexToUpdate = this.requestSource.findIndex(existingRequestModel => existingRequestModel.requestId === model.requestId);
            this.requestSource[indexToUpdate] = model;
        } else {
            this.requestSource.push(model);
        }
        // only needed if CHANGEDETECTION does not work:
        // this.requestSource = Object.assign([], this.requestSource);
        this.dashboardRequests$.next(this.requestSource);

    }

    private assignWorkgroupNames() {
        if (this.requestSource.some(r => r.workgroupGuid)) {
            this.requestSource = this.requestSource.map(r => {
                if (r.workgroupName && r.workgroupName.length > 0) {
                    return r;
                }
                r.workgroupName = this.authenticationService.getWorkGroupName(r.workgroupGuid ?? "");
                return r;
            });
        }
    }


}
